import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { raidApi, type CategoriaRaid, type ContextoTipo } from '../api/raid.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import { MatrizRaid } from '../componentes/raid/MatrizRaid.js';
import { FormularioCriarItemRaid } from '../componentes/raid/FormularioCriarItemRaid.js';

const COR_CATEGORIA: Record<CategoriaRaid, string> = {
  Risk: 'bg-status-vermelho/10 text-status-vermelho',
  Assumption: 'bg-aco/10 text-aco-700',
  Issue: 'bg-status-ambar/10 text-status-ambar',
  Dependency: 'bg-grafite/10 text-grafite',
};

// SPEC-UX §4.7 — lista transversal com filtro por contexto, categoria e
// estado; matriz 5x5 clicável (visão alternativa, só Risk); formulário de
// criação com campos condicionais por categoria (I3/I4).
export function Raid() {
  const [contextoTipo, setContextoTipo] = useState<ContextoTipo>('projeto');
  const [contextoId, setContextoId] = useState('');
  const [visao, setVisao] = useState<'lista' | 'matriz'>('lista');
  const [formularioAberto, setFormularioAberto] = useState(false);
  const queryClient = useQueryClient();

  const consultaAtiva = contextoId.trim().length > 0;
  const { data: itens, isLoading } = useQuery({
    queryKey: ['raid', contextoTipo, contextoId],
    queryFn: () => raidApi.listarPorContexto(contextoTipo, contextoId),
    enabled: consultaAtiva,
  });

  const { data: escalacoes } = useQuery({ queryKey: ['raid', 'escalacoes'], queryFn: raidApi.escalacoesAtivas, enabled: !consultaAtiva });

  const tratar = useMutation({
    mutationFn: (id: string) => raidApi.transicionar(id, 'tratar'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['raid'] }),
  });

  const itensExibidos = consultaAtiva ? itens : escalacoes;

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">RAID</h1>
        {consultaAtiva && (
          <button type="button" onClick={() => setFormularioAberto((v) => !v)} className="rounded bg-aco px-3 py-1.5 text-xs font-medium text-white hover:bg-aco-700">
            {formularioAberto ? 'Fechar formulário' : '+ Novo item'}
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <select
          value={contextoTipo}
          onChange={(e) => setContextoTipo(e.target.value as ContextoTipo)}
          className="rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink"
        >
          <option value="projeto">Projeto</option>
          <option value="programa">Programa</option>
        </select>
        <input
          placeholder="ID do contexto (vazio = escalações ativas)"
          value={contextoId}
          onChange={(e) => setContextoId(e.target.value)}
          className="flex-1 rounded border border-borda bg-base-100 px-3 py-1.5 text-sm text-ink"
        />
      </div>

      {formularioAberto && consultaAtiva && (
        <FormularioCriarItemRaid contextoTipo={contextoTipo} contextoId={contextoId} onCriado={() => setFormularioAberto(false)} />
      )}

      {isLoading && <p className="text-sm text-grafite">Carregando…</p>}

      {!isLoading && (!itensExibidos || itensExibidos.length === 0) && (
        <VazioQueEnsina explicacao="Nenhum item RAID encontrado. Riscos, premissas, issues e dependências aparecem aqui conforme são registrados nos projetos e programas." />
      )}

      {itensExibidos && itensExibidos.length > 0 && (
        <>
          <div className="flex gap-1 border-b border-borda">
            {(['lista', 'matriz'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVisao(v)}
                className={`px-3 py-2 text-sm font-medium capitalize ${visao === v ? 'border-b-2 border-aco text-aco-700' : 'text-grafite'}`}
              >
                {v}
              </button>
            ))}
          </div>

          {visao === 'lista' ? (
            <ul className="flex flex-col gap-2">
              {itensExibidos.map((item) => (
                <li key={item.id} className="flex items-center justify-between rounded border border-borda bg-superficie px-4 py-3">
                  <div>
                    <span className={`fonte-mono mr-2 rounded px-1.5 py-0.5 text-xs ${COR_CATEGORIA[item.categoria]}`}>{item.categoria}</span>
                    <span className="text-sm text-ink">{item.descricao}</span>
                    <p className="text-xs text-grafite">
                      {item.responsavel} · prazo {item.prazo} · {item.estado}
                    </p>
                  </div>
                  {item.estado !== 'Fechado' && item.estado !== 'EmTratamento' && (
                    <button
                      type="button"
                      onClick={() => tratar.mutate(item.id)}
                      className="rounded border border-aco/40 px-3 py-1.5 text-xs font-medium text-aco hover:bg-aco/5"
                    >
                      Tratar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <MatrizRaid itens={itensExibidos} />
          )}
        </>
      )}
    </div>
  );
}
